import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import { getDateRangeBounds, toUTCStartEnd } from "../../utils/dateUtils";
import { DateRangePicker } from "../../components/DateRangePicker";
import { normalizeRow, PaymentRow, PAYMENT_ROW_COLUMNS } from "../billing/paymentFormatting";

// Grouped quick filters over advertiser_payments.status — "Skipped" groups
// the three skip reasons (nothing owed / no card on file / covered by a
// manual payment) into one chip since they all mean "nothing was charged
// this attempt," even though the ledger keeps them distinct for audit (see
// paymentFormatting.jsx's normalizeRow).
const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "succeeded", label: "Succeeded", statuses: ["succeeded"] },
  { id: "failed", label: "Failed", statuses: ["failed"] },
  { id: "pending", label: "Pending", statuses: ["pending"] },
  { id: "skipped", label: "Skipped", statuses: ["skipped_no_amount", "skipped_no_card", "skipped_paid_manually"] },
];

// Full, filterable payment history — reached only via PaymentHistorySection's
// "See more" button (not a top-level nav tab, see MainLayout's TABS/TAB_PATHS).
// View/filter only, no "Make a payment" entry point here — that stays in the
// embedded panel.
export function PaymentsScreen() {
  const { effectiveAdvertiserId: advertiserId } = useAuth();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState("MTD");
  const [appliedCustomStart, setAppliedCustomStart] = useState(null);
  const [appliedCustomEnd, setAppliedCustomEnd] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!supabase || advertiserId == null) {
      setLoading(false);
      setPayments([]);
      setError(advertiserId == null ? "No advertiser linked" : "Supabase not configured");
      return;
    }
    const { startDate, endDate } = getDateRangeBounds(dateRange, appliedCustomStart, appliedCustomEnd);
    if (startDate > endDate) {
      setPayments([]);
      setLoading(false);
      setError(null);
      return;
    }
    const { startISO, endISO } = toUTCStartEnd(startDate, endDate);
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      let query = supabase
        .from("advertiser_payments")
        .select(PAYMENT_ROW_COLUMNS)
        .eq("advertiser_id", advertiserId)
        .neq("status", "draft") // pre-Stripe intent rows (migration 066) are internal only — applies to every chip, incl. "All"
        .gte("created_at", startISO)
        .lte("created_at", endISO)
        .order("created_at", { ascending: false });
      const activeFilter = STATUS_FILTERS.find((f) => f.id === statusFilter);
      if (activeFilter?.statuses) {
        query = activeFilter.statuses.length === 1
          ? query.eq("status", activeFilter.statuses[0])
          : query.in("status", activeFilter.statuses);
      }
      const { data, error: fetchError } = await query;
      if (cancelled) return;
      if (fetchError) {
        setError(fetchError.message || "Failed to load payments");
        setPayments([]);
      } else {
        setPayments((data ?? []).map(normalizeRow));
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [advertiserId, dateRange, appliedCustomStart, appliedCustomEnd, statusFilter]);

  return (
    <div style={{ padding:"28px 28px", maxWidth:1000, margin:"0 auto" }}>
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{ display:"inline-flex", alignItems:"center", gap:6, marginBottom:14, padding:0, background:"transparent", border:"none", color:"#626262", fontSize:12, cursor:"pointer" }}
      >
        ← Back
      </button>

      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:22, flexWrap:"wrap", gap:12 }}>
        <h2 style={{ fontSize:28, fontWeight:600, color:"#151515", margin:0, fontFamily:"'Clash Display Variable','Clash Display',sans-serif" }}>Payments</h2>
        <DateRangePicker
          dateRange={dateRange}
          setDateRange={setDateRange}
          appliedCustomStart={appliedCustomStart}
          setAppliedCustomStart={setAppliedCustomStart}
          appliedCustomEnd={appliedCustomEnd}
          setAppliedCustomEnd={setAppliedCustomEnd}
        />
      </div>

      <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:20 }}>
        {STATUS_FILTERS.map((f) => {
          const active = statusFilter === f.id;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setStatusFilter(f.id)}
              style={{
                padding:"6px 14px", borderRadius:999, fontSize:12, fontWeight:600, cursor:"pointer",
                border: active ? "1px solid #F90091" : "1px solid #E2E2E2",
                background: active ? "#F90091" : "#FFFFFF",
                color: active ? "#FFFFFF" : "#626262",
              }}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div style={{ padding:"40px 0", textAlign:"center", color:"#7b7b7b", fontSize:13 }}>Loading…</div>
      ) : error ? (
        <div style={{ padding:"40px 0", textAlign:"center", color:"#e53935", fontSize:13 }}>{error}</div>
      ) : payments.length === 0 ? (
        <div style={{ padding:"40px 0", textAlign:"center", color:"#7b7b7b", fontSize:13 }}>No payments match your filters.</div>
      ) : (
        payments.map((row) => <PaymentRow key={row.key} row={row} />)
      )}
    </div>
  );
}
